import { useEffect } from 'react';

export interface ToastProps {
  message: string;
  type?: 'success' | 'error' | 'info';
  onClose: () => void;
  duration?: number;
}

export function Toast({ message, type = 'info', onClose, duration = 5000 }: ToastProps) {
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(onClose, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  const colors = {
    success: 'bg-[#e1e5eb] border-[#d1d8e3] text-[#424a47]',
    error: 'bg-[#fff1e6] border-[#f0d8c1] text-[#424a47]',
    info: 'bg-[#e8d8c9] border-[#e5e2dc] text-[#424a47]',
  };

  const icons = {
    success: (
      <svg
        className="w-[18px] h-[18px] text-[#4b607f]"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
      </svg>
    ),
    error: (
      <svg
        className="w-[18px] h-[18px] text-[#f3701e]"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
    info: (
      <svg
        className="w-[18px] h-[18px] text-[#68716d]"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
  };

  return (
    <div
      className={`fixed top-4 right-4 z-[100] min-w-[320px] max-w-[480px] p-4 rounded-[13px] shadow-[0_4px_12px_rgba(0,0,0,0.15)] border flex items-start gap-3 animate-slide-in ${colors[type]}`}
      role="alert"
    >
      <div className="flex-shrink-0 w-[20px] h-[20px] rounded-full bg-white flex items-center justify-center">
        {icons[type]}
      </div>
      <p className="flex-1 text-[14px] leading-[1.5]">{message}</p>
      <button
        onClick={onClose}
        className="flex-shrink-0 text-[#68716d] hover:text-[#424a47] transition-colors"
        aria-label="Close"
      >
        <svg
          className="w-[18px] h-[18px]"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      <style>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

// Toast container to manage multiple toasts
export function ToastContainer({
  toasts,
  removeToast,
}: {
  toasts: Array<{ id: string; message: string; type?: 'success' | 'error' | 'info' }>;
  removeToast: (id: string) => void;
}) {
  return (
    <div className="fixed top-4 right-4 z-[100] flex flex-col gap-3 pointer-events-none">
      {toasts.map((toast, index) => (
        <div
          key={toast.id}
          style={{ pointerEvents: 'auto', transform: `translateY(${index * 8}px)` }}
        >
          <Toast message={toast.message} type={toast.type} onClose={() => removeToast(toast.id)} />
        </div>
      ))}
    </div>
  );
}
